import React from 'react'
import { MinusOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Form, InputNumber, Select, Space, Spin, Typography, message } from 'antd'
import debounce from 'lodash.debounce'
import { appApi } from 'api/app'
import { sellerApi } from 'api/seller'
import { IAddressSuggestion } from 'models/app'
import { IOwnDelivery, IOwnDeliveryZone } from 'models/seller'
import { DEBOUNCE_DELAY } from 'shared/constants/app'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import { loadYandexMaps } from 'shared/utils/yandexMaps'
import styles from './SellerDelivery.module.scss'

type TCoordinates = [number, number]
type TZoneCenter = Omit<IOwnDeliveryZone, 'radius_km'>

interface IOwnDeliveryFormValues {
  max_length_cm: number
  max_width_cm: number
  max_height_cm: number
  max_weight_kg: number
  radius_km: number
}

interface IOwnDeliveryFormProps {
  sellerId: string
  ownDelivery: IOwnDelivery
  onSaved: (ownDelivery: IOwnDelivery) => void
  onCancel: () => void
}

interface IYandexMap {
  destroy: () => void
  setBounds: (
    bounds: number[][],
    options?: Record<string, unknown>
  ) => Promise<unknown>
  events: {
    add: (name: string, handler: (event: { get: (name: string) => unknown }) => void) => void
  }
  geoObjects: {
    add: (object: unknown) => void
    remove: (object: unknown) => void
  }
}

interface IYandexCircle {
  geometry: { getBounds: () => number[][] }
}

const MAP_CONTAINER_ID = 'own-delivery-zone-map'
const DEFAULT_CENTER: TCoordinates = [55.76, 37.64]
const DEFAULT_ZOOM = 9
const DEFAULT_RADIUS_KM = 20
const MIN_RADIUS_KM = 1
const MAX_RADIUS_KM = 500
const RADIUS_STEP_KM = 5

const getSuggestionCenter = (item: IAddressSuggestion): TZoneCenter | null => {
  const latitude = Number(item.data.geo_lat)
  const longitude = Number(item.data.geo_lon)
  if (!item.data.geo_lat || !item.data.geo_lon || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null
  }
  return { latitude, longitude, address: item.value }
}

export const OwnDeliveryForm: React.FC<IOwnDeliveryFormProps> = ({
  sellerId,
  ownDelivery,
  onSaved,
  onCancel
}) => {
  const [form] = Form.useForm<IOwnDeliveryFormValues>()
  const radiusKm = Form.useWatch('radius_km', form)
  const savedSettings = ownDelivery.settings

  const mapRef = React.useRef<IYandexMap | null>(null)
  const zoneObjectsRef = React.useRef<unknown[]>([])
  const [isMapReady, setIsMapReady] = React.useState(false)
  const [isGeocoding, setIsGeocoding] = React.useState(false)
  const [isSaving, setIsSaving] = React.useState(false)
  const [suggestions, setSuggestions] = React.useState<IAddressSuggestion[]>([])
  const [center, setCenter] = React.useState<TZoneCenter | null>(
    savedSettings
      ? {
        latitude: savedSettings.zone.latitude,
        longitude: savedSettings.zone.longitude,
        address: savedSettings.zone.address
      }
      : null
  )

  // Адрес точки берём у DaData, как и подсказки: геокодер Яндекса нашим ключом не открыт
  const selectMapPoint = React.useCallback(async (coords: TCoordinates) => {
    setIsGeocoding(true)
    try {
      const { data } = await appApi.getAddressByGeo({
        lat: coords[0],
        lon: coords[1],
        count: 1,
        radius_meters: 1000
      })
      const address = data.suggestions[0]?.value
      if (!address) {
        showMessage('error', 'Рядом с точкой нет адреса', 'Нажмите ближе к дороге или найдите адрес в поиске')
        return
      }
      setCenter({ latitude: coords[0], longitude: coords[1], address })
    } catch (error) {
      showServerError(error)
    } finally {
      setIsGeocoding(false)
    }
  }, [])

  const selectMapPointRef = React.useRef(selectMapPoint)
  React.useEffect(() => {
    selectMapPointRef.current = selectMapPoint
  }, [selectMapPoint])

  React.useEffect(() => {
    let isDestroyed = false

    const initMap = async () => {
      try {
        await loadYandexMaps()
        if (isDestroyed) return
        if (!window.ymaps) {
          showMessage('error', 'Не удалось загрузить карту', 'Обновите страницу')
          return
        }
        window.ymaps.ready(() => {
          if (isDestroyed || mapRef.current) return
          const map = new window.ymaps.Map(MAP_CONTAINER_ID, {
            center: DEFAULT_CENTER,
            zoom: DEFAULT_ZOOM,
            controls: ['geolocationControl', 'zoomControl', 'fullscreenControl']
          }) as IYandexMap
          map.events.add('click', (event) => {
            selectMapPointRef.current(event.get('coords') as TCoordinates)
          })
          mapRef.current = map
          setIsMapReady(true)
        })
      } catch {
        showMessage('error', 'Не удалось загрузить карту', 'Обновите страницу')
      }
    }

    initMap()
    return () => {
      isDestroyed = true
      mapRef.current?.destroy()
      mapRef.current = null
    }
  }, [])

  React.useEffect(() => {
    const map = mapRef.current
    if (!isMapReady || !map || !window.ymaps) return

    zoneObjectsRef.current.forEach((object) => map.geoObjects.remove(object))
    zoneObjectsRef.current = []
    if (!center) return

    const point: TCoordinates = [center.latitude, center.longitude]
    const radius = Number(radiusKm) > 0 ? Number(radiusKm) : DEFAULT_RADIUS_KM
    const circle = new window.ymaps.Circle(
      [point, radius * 1000],
      { hintContent: `До ${radius} км` },
      {
        fillColor: '#1677ff22',
        strokeColor: '#1677ff',
        strokeOpacity: 0.9,
        strokeWidth: 2,
        // клик сквозь круг ставит новый центр
        interactivityModel: 'default#transparent'
      }
    ) as IYandexCircle
    const placemark = new window.ymaps.Placemark(point, { hintContent: center.address })
    map.geoObjects.add(circle)
    map.geoObjects.add(placemark)
    zoneObjectsRef.current = [circle, placemark]
    void map.setBounds(circle.geometry.getBounds(), {
      checkZoomRange: true,
      zoomMargin: 24
    })
  }, [center, isMapReady, radiusKm])

  const getSuggestions = React.useCallback(async (search: string) => {
    if (!search.trim()) {
      setSuggestions([])
      return
    }
    try {
      const { data } = await appApi.getAddresses({ address: search })
      setSuggestions(data.suggestions)
    } catch (error) {
      showServerError(error)
    }
  }, [])

  const debouncedGetSuggestions = React.useMemo(
    () => debounce(getSuggestions, DEBOUNCE_DELAY),
    [getSuggestions]
  )
  React.useEffect(() => () => debouncedGetSuggestions.cancel(), [debouncedGetSuggestions])

  const selectSuggestion = (value: string) => {
    const item = suggestions.find((suggestion) => suggestion.value === value)
    const nextCenter = item ? getSuggestionCenter(item) : null
    if (!nextCenter) {
      showMessage('error', 'У адреса нет точки на карте', 'Уточните адрес или нажмите на карту')
      return
    }
    setCenter(nextCenter)
  }

  const changeRadius = (delta: number) => {
    const radius = Number(form.getFieldValue('radius_km')) || 0
    form.setFields([{
      name: 'radius_km',
      value: Math.min(MAX_RADIUS_KM, Math.max(MIN_RADIUS_KM, radius + delta)),
      errors: []
    }])
  }

  const save = async () => {
    let values: IOwnDeliveryFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    if (!center) {
      showMessage('error', 'Укажите центр зоны доставки', 'Найдите адрес или нажмите на карту')
      return
    }
    setIsSaving(true)
    try {
      const { radius_km, ...limits } = values
      const { data } = await sellerApi.updateOwnDelivery({
        seller_id: sellerId,
        enabled: true,
        settings: { ...limits, zone: { ...center, radius_km } }
      })
      message.success(
        ownDelivery.enabled ? 'Данные успешно обновлены' : 'Самостоятельная доставка включена'
      )
      onSaved(data)
    } catch (error) {
      showServerError(error)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className={styles.ownDelivery}>
      <Typography.Paragraph type='secondary'>
        Покупатель увидит способ «Доставка продавцом», если каждый товар заказа
        помещается в ваши габариты, заказ не тяжелее вашего лимита, а адрес
        покупателя — внутри зоны. Для покупателя доставка бесплатная. После
        передачи заказа в доставку свяжитесь с покупателем и договоритесь о
        времени.
      </Typography.Paragraph>
      <Form
        form={form}
        layout='vertical'
        requiredMark={false}
        initialValues={{
          max_length_cm: savedSettings?.max_length_cm,
          max_width_cm: savedSettings?.max_width_cm,
          max_height_cm: savedSettings?.max_height_cm,
          max_weight_kg: savedSettings?.max_weight_kg,
          radius_km: savedSettings?.zone.radius_km ?? DEFAULT_RADIUS_KM
        }}
      >
        <Typography.Text strong>
          Габариты одной единицы товара с упаковкой и вес всего заказа
        </Typography.Text>
        <div className={styles.ownDeliveryRow}>
          <Form.Item
            name='max_length_cm'
            label='Длина, см'
            rules={[{ required: true, message: 'Укажите длину' }]}
          >
            <InputNumber min={1} max={1000} precision={0} className={styles.ownDeliveryNumber} />
          </Form.Item>
          <Form.Item
            name='max_width_cm'
            label='Ширина, см'
            rules={[{ required: true, message: 'Укажите ширину' }]}
          >
            <InputNumber min={1} max={1000} precision={0} className={styles.ownDeliveryNumber} />
          </Form.Item>
          <Form.Item
            name='max_height_cm'
            label='Высота, см'
            rules={[{ required: true, message: 'Укажите высоту' }]}
          >
            <InputNumber min={1} max={1000} precision={0} className={styles.ownDeliveryNumber} />
          </Form.Item>
          <Form.Item
            name='max_weight_kg'
            label='Вес заказа, кг'
            rules={[{ required: true, message: 'Укажите вес' }]}
          >
            <InputNumber
              min={0.1}
              max={10000}
              step={0.5}
              precision={1}
              decimalSeparator=','
              className={styles.ownDeliveryNumber}
            />
          </Form.Item>
        </div>
        <Form.Item
          label='Центр зоны доставки'
          extra='Найдите адрес или нажмите на карту. Покупатели не увидят этот адрес, но увидят зону доставки'
        >
          <Select
            showSearch
            value={center?.address}
            placeholder='Начните вводить адрес'
            notFoundContent={null}
            filterOption={false}
            onSearch={debouncedGetSuggestions}
            onSelect={selectSuggestion}
            options={suggestions.map((item) => ({
              value: item.value,
              label: item.value
            }))}
          />
        </Form.Item>
        <Form.Item label='Радиус зоны, км'>
          <Space.Compact>
            <Button
              htmlType='button'
              icon={<MinusOutlined />}
              aria-label={`Уменьшить радиус на ${RADIUS_STEP_KM} км`}
              onClick={() => changeRadius(-RADIUS_STEP_KM)}
            />
            <Form.Item
              name='radius_km'
              noStyle
              rules={[{ required: true, message: 'Укажите радиус' }]}
            >
              <InputNumber
                min={MIN_RADIUS_KM}
                max={MAX_RADIUS_KM}
                step={RADIUS_STEP_KM}
                precision={0}
                controls={false}
                className={styles.ownDeliveryRadius}
              />
            </Form.Item>
            <Button
              htmlType='button'
              icon={<PlusOutlined />}
              aria-label={`Увеличить радиус на ${RADIUS_STEP_KM} км`}
              onClick={() => changeRadius(RADIUS_STEP_KM)}
            />
          </Space.Compact>
        </Form.Item>
      </Form>
      <div className={styles.ownDeliveryMapWrapper}>
        <div
          id={MAP_CONTAINER_ID}
          className={styles.ownDeliveryMap}
          role='region'
          aria-label='Зона самостоятельной доставки'
        />
        {(!isMapReady || isGeocoding) && (
          <div className={styles.ownDeliveryMapLoading}>
            <Spin />
          </div>
        )}
      </div>
      <div className={styles.ownDeliveryActions}>
        <Button type='primary' htmlType='button' loading={isSaving} onClick={save}>
          {ownDelivery.enabled ? 'Сохранить' : 'Сохранить и включить'}
        </Button>
        {!ownDelivery.enabled && (
          <Button htmlType='button' disabled={isSaving} onClick={onCancel}>
            Отмена
          </Button>
        )}
      </div>
    </div>
  )
}
